import { OptionType, StationFilterOption } from '@app/components/stations/advanced-filter/types';
import { Station } from '@app/core/types/stations';
import {
  AuditReportsGipReportDataDto,
  StationsFiltersTypeEnum10,
  StationsFiltersTypeEnum11,
  StationsFiltersTypeEnum12,
} from '@mi-front/api-v2';
import { DateTimeStr, formatDate, getBrowserTimezone } from '@mi-front/core';
import moment from 'moment-timezone';
import { GipReportValues } from '../form/types';
import { areDatesDisabledForReport } from './helpers';

/**
 * Maps form values to the GIP report API DTO.
 * Date walls are interpreted in `tz` (station context or browser fallback) so
 * day boundaries match the form fields that also use useTimezone().
 */
export function mapValuesToGipReportDataDto(
  clientId: string,
  values: GipReportValues,
  stations: readonly Station[],
  tz: string = getBrowserTimezone(),
): AuditReportsGipReportDataDto {
  const datesDisabled = areDatesDisabledForReport(values);

  /** Formats an ISO instant as a timezone-naive wall-clock string in `tz`. */
  function transformDateWithoutTz(date: string): DateTimeStr {
    return formatDate(date, 'YYYY-MM-DDTHH:mm:ss', tz) as DateTimeStr;
  }

  const useFullDateRange = datesDisabled && !values._fixedDateRange;

  // Handle station selection based on selection type
  let stationsIds: string[] | undefined;
  let stationsFilters: AuditReportsGipReportDataDto['stationsFilters'] | undefined;

  if (values.stationsSelectionType === 'advanced') {
    // Use advanced filtering
    stationsFilters =
      values.stationsFilter?.map((filter: StationFilterOption) => {
        switch (filter.type) {
          case OptionType.StationType:
            return {
              type: StationsFiltersTypeEnum10.STATION_TYPE,
              value: filter.value,
            };
          case OptionType.StationGroup:
            return {
              type: StationsFiltersTypeEnum11.STATION_GROUP,
              value: filter.value.id,
            };
          case OptionType.IndividualStation:
            return {
              type: StationsFiltersTypeEnum12.STATION_INDIVIDUAL,
              value: filter.value.id,
            };
          default:
            throw new Error(`Unknown station filter type: ${JSON.stringify(filter)}`);
        }
      }) ?? [];
  } else {
    // Use simple station selection (fallback to all stations if none selected)
    stationsIds = (values.stations?.length ? values.stations : stations).map((s) => s.id);
  }

  return {
    clientId,
    stationsIds,
    stationsFilters,
    from: useFullDateRange
      ? ('2020-01-01T00:00:00Z' as DateTimeStr)
      : transformDateWithoutTz(values.from),
    to: useFullDateRange
      ? (moment().tz(tz).endOf('day').toISOString() as DateTimeStr)
      : transformDateWithoutTz(values.to),
    campaigns: values.campaigns.map((v) => v.name),
    advertisers: values.advertisers.map((v) => v.name),
    versions: values.versions.map((v) => v.name),
    customFields: values.customFieldKey
      ? {
          [values.customFieldKey.name]: values.customFieldValue,
        }
      : {},
    customIds: values.customIds.map((v) => v.customId),
    orders: values.orders,
    excludedMatches: values.excludedMatches,
  };
}
